import React, { useState, useEffect } from 'react';
import { Grid3X3, ChevronRight } from 'lucide-react';
import type { FacetBucket } from '../../utils/types';
import { tileGridStyle } from '../../utils/gridStyles';
import { getCatalogFacets, getPublicCategories, type CollectionCategory } from '../../utils/api';
import { CollectionTiles } from '../CollectionTiles';
import { useFontSizes } from '../../contexts/FontSizeContext';
import { useI18n } from '../../i18n/I18nContext';
import { translateFacetLabel } from '../../i18n/index';
import type { WidgetProps } from './types';

/** Stable gradient palette cycled across categories */
const GRADIENTS = [
  'from-red-800 to-red-900',
  'from-amber-100 to-amber-200',
  'from-pink-300 to-pink-400',
  'from-purple-700 to-purple-800',
  'from-orange-300 to-orange-400',
  'from-emerald-600 to-emerald-700',
  'from-sky-400 to-sky-500',
  'from-yellow-200 to-yellow-300',
  'from-indigo-600 to-indigo-700',
  'from-rose-400 to-rose-500',
  'from-teal-500 to-teal-600',
  'from-slate-600 to-slate-700',
];

/** Indices whose gradient is light (dark text needed) */
const LIGHT_GRADIENT_INDICES = new Set([1, 4, 7]);

export const CategoriesWidget: React.FC<WidgetProps> = ({ config, onApplyFilters, itemsPerRow }) => {
  const { heading, body, small } = useFontSizes();
  const { t, lang } = useI18n();
  const [categories, setCategories] = useState<FacetBucket[]>([]);
  const [collections, setCollections] = useState<CollectionCategory[]>([]);

  useEffect(() => {
    // Curated collections (imported by a category feed, e.g. crawled
    // botilia.gr "wine ideas") take precedence — they have real images,
    // titles and destination URLs. Product-category facets are the fallback.
    getPublicCategories(lang).then((cols) => {
      if (cols.length > 0) setCollections(cols);
    });
    getCatalogFacets(lang).then((facets) => {
      if (facets?.categories && Array.isArray(facets.categories)) {
        setCategories(facets.categories as FacetBucket[]);
      }
    });
  }, [lang]);

  // --- Curated collections: image tiles linking to the collection page ---
  if (collections.length > 0) {
    const visibleCols = config.maxItems ? collections.slice(0, config.maxItems) : collections;
    return (
      <section className="mb-4 sm:mb-8">
        <h2 className={`flex items-center gap-2 ${heading} font-semibold uppercase tracking-wider text-[#020202] mb-2 sm:mb-4`}>
          <Grid3X3 className="h-4 w-4" />
          {config.title ?? t('section.categories')}
        </h2>
        <CollectionTiles collections={visibleCols} itemsPerRow={itemsPerRow ?? 4} />
      </section>
    );
  }

  if (categories.length === 0) return null;

  const visible = config.maxItems ? categories.slice(0, config.maxItems) : categories;

  return (
    <section className="mb-4 sm:mb-8">
      <h2 className={`flex items-center gap-2 ${heading} font-semibold uppercase tracking-wider text-[#020202] mb-2 sm:mb-4`}>
        <Grid3X3 className="h-4 w-4" />
        {config.title ?? t('section.categories')}
      </h2>
      <div style={tileGridStyle('small', itemsPerRow ?? 4, 12)}>
        {visible.map((cat, index) => {
          const gradient = GRADIENTS[index % GRADIENTS.length];
          const isTextDark = LIGHT_GRADIENT_INDICES.has(index % GRADIENTS.length);
          return (
            <button
              key={cat.value}
              type="button"
              onClick={() => onApplyFilters?.({ category: cat.value })}
              className={[
                'group relative flex flex-col items-center justify-center p-3 sm:p-5 transition-all',
                'bg-gradient-to-br', gradient,
                'focus:outline-none focus:ring-2 focus:ring-brand/30 focus:ring-offset-2',
                'animate-fade-in',
              ].join(' ')}
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <span className={[
                `${body} font-semibold`,
                isTextDark ? 'text-gray-800' : 'text-white',
              ].join(' ')}>
                {translateFacetLabel(lang, cat.label)}
              </span>
              <span className={[
                `${small} mt-1`,
                isTextDark ? 'text-gray-600' : 'text-white/70',
              ].join(' ')}>
                {cat.count}
              </span>
              <ChevronRight className={[
                'absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity',
                isTextDark ? 'text-gray-600' : 'text-white/80',
              ].join(' ')} />
            </button>
          );
        })}
      </div>
    </section>
  );
};
